// ============================================================
//  tienda.js — Carrito + Modal + Filtros — Dulet International Group
//  CATÁLOGO 100% DINÁMICO — alimentado desde la BD vía webhook
//  GEOLOCALIZACIÓN — precios por país (MXN / USD / GTQ)
// ============================================================

const CATALOGO = {}; // Se llena dinámicamente desde fetchProductosPublicados()

// ─── Core UI & Carrito (Hoisted para acceso global y cache-safety) ────────────
function actualizarCarrito() {
  actualizarUI();
}

function actualizarUI() {
  renderizarCarrito();
  actualizarContador();
}

// Helper: quitar sufijos de nivel de precio (Pub, Dis, MC, PUB, DIS, etc.) para display público
function limpiarNombreProducto(nombre) {
  if (!nombre) return '';
  // Quitar sufijos de nivel al final del nombre: " Pub", " PUB", " Dis", " DIS", " MC", " mc", " PB"
  return nombre.replace(/\s+(Pub|PUB|PB|Dis|DIS|MC|mc|Mc)\s*$/i, '').trim();
}

// ─── Geolocalización & Precios Regionales ─────────────────────────────────────
// Mapeo de país → campo de precio, moneda, símbolo
const REGION_MAP = {
  MX: { campo: 'precio_mxn', moneda: 'MXN', simbolo: '$',  activo: 'activo_mex', bandera: '🇲🇽', nombre: 'México' },
  US: { campo: 'precio_usd', moneda: 'USD', simbolo: '$',  activo: 'activo_usa', bandera: '🇺🇸', nombre: 'Estados Unidos' },
  GT: { campo: 'precio_gtq', moneda: 'GTQ', simbolo: 'Q',  activo: 'activo_gtq', bandera: '🇬🇹', nombre: 'Guatemala' },
};

// Región detectada (default: México, US en inglés)
let defaultRegionCode = (document.documentElement.lang === 'en' || window.location.pathname.includes('/us/') || window.location.pathname.includes('/en/') || window.location.pathname.includes('/english')) ? 'US' : 'MX';
let REGION = REGION_MAP[defaultRegionCode] || REGION_MAP.MX;
let REGION_CODE = defaultRegionCode;
let geoDetected = false;

// Detectar ubicación del visitante por IP
async function detectarUbicacion() {
  // Primero verificar si hay una selección manual guardada
  const manual = localStorage.getItem('dulet_region');
  if (manual && REGION_MAP[manual]) {
    REGION_CODE = manual;
    REGION = REGION_MAP[manual];
    geoDetected = true;
    console.log(`🌍 Región manual: ${REGION.bandera} ${REGION.nombre}`);
    window.dispatchEvent(new CustomEvent('regionDetected', { detail: { code: REGION_CODE } }));
    return;
  }

  // Intentar detección automática por IP
  const apis = [
    { url: 'https://ipapi.co/json/', parse: d => d.country_code },
    { url: 'https://ip-api.com/json/?fields=countryCode', parse: d => d.countryCode },
  ];

  for (const api of apis) {
    try {
      const ctrl = new AbortController();
      const timeout = setTimeout(() => ctrl.abort(), 3000);
      const res = await fetch(api.url, { signal: ctrl.signal });
      clearTimeout(timeout);
      const data = await res.json();
      const code = api.parse(data);

      if (code && REGION_MAP[code]) {
        REGION_CODE = code;
        REGION = REGION_MAP[code];
        geoDetected = true;
        console.log(`🌍 Ubicación detectada: ${REGION.bandera} ${REGION.nombre}`);
        window.dispatchEvent(new CustomEvent('regionDetected', { detail: { code: REGION_CODE } }));
        return;
      }
    } catch (e) {
      // Silently try next API
    }
  }

  // Fallback: Default
  console.log('🌍 Geolocalización no disponible — usando fallback: ' + defaultRegionCode);
  window.dispatchEvent(new CustomEvent('regionDetected', { detail: { code: defaultRegionCode } }));
}


// Helper: obtener precio regional de un producto de la BD
function precioRegional(dbP) {
  // 1. Intentar campo regional exacto (precio_mxn, precio_usd, precio_gtq)
  const val = parseFloat(dbP[REGION.campo]);
  if (!isNaN(val) && val > 0) return val;

  // 2. Si estamos en MX, usar campo genérico 'precio' como fallback
  if (REGION_CODE === 'MX') {
    const fallback = parseFloat(dbP.precio);
    if (!isNaN(fallback) && fallback > 0) return fallback;
  }

  // 3. No disponible en esta región
  return 0;
}

// Helper: verificar si producto está activo en la región
function productoActivoEnRegion(dbP) {
  // Si el campo activo_xxx existe, respetar
  if (REGION.activo in dbP) {
    return !!dbP[REGION.activo];
  }
  // Si no existe el campo, asumir activo
  return true;
}

// Helper: formatear precio con moneda regional
function formatPrecioRegional(monto) {
  if (REGION.moneda === 'GTQ') {
    return `Q${monto.toLocaleString('es-GT')} GTQ`;
  }
  return `${REGION.simbolo}${monto.toLocaleString('es-MX')} ${REGION.moneda}`;
}

// ─── Estado del carrito ───────────────────────────────────────────────────────
let carrito = JSON.parse(localStorage.getItem('dulet_carrito') || '{}');
let _enviosDisponibles = []; // Productos de categoría 'envios' cargados del BD

function guardarCarrito() {
  localStorage.setItem('dulet_carrito', JSON.stringify(carrito));
}

// ─── Agregar al carrito ───────────────────────────────────────────────────────
function agregarAlCarrito(id, cantidad = 1) {
  let realId = CATALOGO[id]?.carritoId || id;
  if (!CATALOGO[realId] && !CATALOGO[id]) {
    const foundKey = Object.keys(CATALOGO).find(k => k === id || CATALOGO[k].sku === id || String(CATALOGO[k].producto_id) === String(id) || CATALOGO[k].carritoId === id);
    if (foundKey) {
      realId = foundKey;
    } else {
      console.warn(`[Carrito] Producto no encontrado en CATALOGO: ${id}`);
      return;
    }
  }
  const usedId = CATALOGO[realId] ? realId : (CATALOGO[id] ? id : realId);
  carrito[usedId] = (carrito[usedId] || 0) + cantidad;
  guardarCarrito();
  actualizarUI();
  abrirCarrito();
  const prod = CATALOGO[usedId] || CATALOGO[id];
  mostrarToastCarrito(prod?.nombre || 'Producto');
}

function quitarDelCarrito(id) {
  delete carrito[id];
  guardarCarrito();
  renderizarCarrito();
  actualizarContador();
}

function cambiarCantidad(id, delta) {
  carrito[id] = (carrito[id] || 0) + delta;
  if (carrito[id] <= 0) delete carrito[id];
  guardarCarrito();
  renderizarCarrito();
  actualizarContador();
}

// ─── UI ───────────────────────────────────────────────────────────────────────
function actualizarUI() {
  renderizarCarrito();
  actualizarContador();
}

function actualizarCarrito() {
  actualizarUI();
}

function actualizarContador() {
  const total = Object.values(carrito).reduce((a, b) => a + b, 0);
  const badge = document.getElementById('cart-badge');
  if (badge) {
    badge.textContent = total;
    badge.style.display = total > 0 ? 'flex' : 'none';
  }
  const altBadge = document.getElementById('cart-count');
  if (altBadge && altBadge !== badge) {
    altBadge.textContent = total;
    altBadge.style.display = total > 0 ? 'flex' : 'none';
  }
  const mobileBadge = document.getElementById('mobile-cart-badge');
  if (mobileBadge) {
    mobileBadge.textContent = total;
    mobileBadge.style.display = total > 0 ? 'inline-block' : 'none';
  }
}

function renderizarCarrito() {
  const lista = document.getElementById('cart-items');
  const totalEl = document.getElementById('cart-total');
  const emptyEl = document.getElementById('cart-empty');
  const actionsEl = document.getElementById('cart-actions');
  if (!lista) return;

  const ids = Object.keys(carrito);
  if (ids.length === 0) {
    lista.innerHTML = '';
    if (emptyEl) emptyEl.style.display = 'flex';
    if (actionsEl) actionsEl.style.display = 'none';
    if (totalEl) totalEl.textContent = formatPrecioRegional(0);
    return;
  }

  if (emptyEl) emptyEl.style.display = 'none';
  if (actionsEl) actionsEl.style.display = 'flex';

  let totalPrecio = 0;
  lista.innerHTML = ids.map((id) => {
    let p = CATALOGO[id];
    if (!p) {
      const foundKey = Object.keys(CATALOGO).find(k => k === id || CATALOGO[k].sku === id || String(CATALOGO[k].producto_id) === String(id) || CATALOGO[k].carritoId === id);
      if (foundKey) p = CATALOGO[foundKey];
    }
    if (!p) return '';
    const qty = carrito[id];
    totalPrecio += (p.precio || 0) * qty;
    return `
      <div class="cart-item" id="cart-item-${id}">
        <img src="${p.img}" alt="${p.nombre}" class="cart-item-img" onerror="this.style.display='none'">
        <div class="cart-item-info">
          <span class="cart-item-name">${p.emoji} ${p.nombre}</span>
          <span class="cart-item-detail">${p.detalle}</span>
          <span class="cart-item-price">${formatPrecioRegional(p.precio)} c/u</span>
        </div>
        <div class="cart-item-controls">
          <button class="cart-qty-btn" onclick="cambiarCantidad('${id}', -1)">−</button>
          <span class="cart-qty">${qty}</span>
          <button class="cart-qty-btn" onclick="cambiarCantidad('${id}', 1)">+</button>
          <button class="cart-remove-btn" onclick="quitarDelCarrito('${id}')" title="Eliminar">🗑</button>
        </div>
      </div>
    `;
  }).join('');

  if (totalEl) totalEl.textContent = formatPrecioRegional(totalPrecio);
}

// ─── Abrir / Cerrar carrito ───────────────────────────────────────────────────
function abrirCarrito() {
  cerrarProductoBtn();
  if (typeof window.closeMobileDrawer === 'function') {
    window.closeMobileDrawer();
  }
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');
  if (overlay) {
    overlay.style.display = 'block';
    overlay.style.pointerEvents = 'auto';
    overlay.style.visibility = 'visible';
    void overlay.offsetWidth;
    overlay.classList.add('visible');
  }
  if (drawer) {
    drawer.style.display = 'flex';
    drawer.style.pointerEvents = 'auto';
    drawer.style.visibility = 'visible';
    void drawer.offsetWidth;
    drawer.classList.add('open');
  }
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';

  // Ocultar Chatbot para evitar superposición de clics con los botones del carrito
  const chatbot = document.getElementById('chatbotContainer');
  if (chatbot) chatbot.style.display = 'none';
}

function cerrarCarrito() {
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');
  if (drawer) {
    drawer.classList.remove('open');
    drawer.style.pointerEvents = 'none';
    drawer.style.visibility = 'hidden';
    drawer.style.display = 'none';
  }
  if (overlay) {
    overlay.classList.remove('visible');
    overlay.style.pointerEvents = 'none';
    overlay.style.visibility = 'hidden';
    overlay.style.display = 'none';
  }
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  document.body.style.removeProperty('overflow');
  document.documentElement.style.removeProperty('overflow');

  // Restaurar Chatbot
  const chatbot = document.getElementById('chatbotContainer');
  if (chatbot) chatbot.style.display = '';
}

// ─── Toast de confirmación ────────────────────────────────────────────────────
function mostrarToastCarrito(nombre) {
  const toast = document.getElementById('cart-toast');
  if (!toast) return;
  const msgEl = toast.querySelector('.cart-toast-msg') || toast;
  msgEl.textContent = `✅ "${nombre || 'Producto'}" agregado al carrito`;
  toast.classList.add('show');
  setTimeout(() => toast.classList.remove('show'), 3000);
}

// ─── MODAL DE PRODUCTO ────────────────────────────────────────────────────────
function abrirProducto(id) {
  const p = CATALOGO[id];
  if (!p) return;

  const modal = document.getElementById('productoModal');
  const overlay = document.getElementById('productoModalOverlay');
  if (!modal || !overlay) {
    // Si no hay modal en el DOM (ej. página en construcción), agregar directo al carrito
    agregarAlCarrito(p.carritoId || id);
    return;
  }

  // Imagen principal
  const imgEl = document.getElementById('modalImgPrincipal');
  if (imgEl && p.imgs && p.imgs.length > 0) {
    imgEl.src = p.imgs[0];
    imgEl.alt = p.nombre;
  }

  // Thumbnails
  const thumbsEl = document.getElementById('modalThumbs');
  if (thumbsEl && p.imgs) {
    thumbsEl.innerHTML = p.imgs.map((url, i) =>
      `<img src="${url}" alt="${p.nombre} ${i+1}" class="modal-thumb ${i === 0 ? 'active' : ''}"
       onclick="cambiarImgModal(this, '${url}')">`
    ).join('');
  }

  // Info
  const catEl = document.getElementById('modalCat');
  if (catEl) catEl.textContent = p.categoria;
  const nomEl = document.getElementById('modalNombre');
  if (nomEl) nomEl.textContent = p.nombre;
  const preEl = document.getElementById('modalPrecio');
  if (preEl) preEl.textContent = formatPrecioRegional(p.precio);
  const descEl = document.getElementById('modalDescripcion');
  if (descEl) descEl.textContent = p.descripcion;

  const featEl = document.getElementById('modalFeatures');
  if (featEl) {
    featEl.innerHTML = (p.features || []).map(f => `<li>✓ ${f}</li>`).join('');
  }

  // Botón agregar
  const btnAgregar = document.getElementById('modalBtnAgregar');
  const carritoId = p.carritoId || id;
  if (btnAgregar) {
    btnAgregar.onclick = (e) => {
      if (e) { e.stopPropagation(); e.preventDefault(); }
      cerrarProductoBtn();
      setTimeout(() => {
        agregarAlCarrito(carritoId);
      }, 50);
    };
  }

  // Botón tienda
  const btnTienda = document.getElementById('modalBtnTienda');
  if (btnTienda) btnTienda.href = p.shopUrl || 'https://www.duletbio.com.mx/collections/all';

  // Abrir modal
  overlay.style.display = 'block';
  overlay.style.pointerEvents = 'auto';
  overlay.style.visibility = 'visible';
  modal.style.display = 'block';
  modal.style.pointerEvents = 'auto';
  modal.style.visibility = 'visible';
  void overlay.offsetWidth;
  void modal.offsetWidth;
  modal.classList.add('open');
  overlay.classList.add('visible');
  document.body.style.overflow = 'hidden';
}

function cambiarImgModal(thumbEl, url) {
  document.getElementById('modalImgPrincipal').src = url;
  document.querySelectorAll('.modal-thumb').forEach(t => t.classList.remove('active'));
  thumbEl.classList.add('active');
}

function cerrarProducto(e) {
  if (e.target === document.getElementById('productoModalOverlay')) {
    cerrarProductoBtn();
  }
}

function cerrarProductoBtn() {
  const modal = document.getElementById('productoModal');
  const overlay = document.getElementById('productoModalOverlay');
  if (modal) {
    modal.classList.remove('open');
    modal.style.pointerEvents = 'none';
    modal.style.visibility = 'hidden';
    modal.style.display = 'none';
  }
  if (overlay) {
    overlay.classList.remove('visible');
    overlay.style.pointerEvents = 'none';
    overlay.style.visibility = 'hidden';
    overlay.style.display = 'none';
  }
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  document.body.style.removeProperty('overflow');
  document.documentElement.style.removeProperty('overflow');
}

// ─── FILTROS DE CATEGORÍA ─────────────────────────────────────────────────────
function initFiltros() {
  const btns = document.querySelectorAll('.filtro-btn');
  btns.forEach(btn => {
    // Clone to remove any previously stacked listeners
    const newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);
  });
  // Re-query after cloning
  const freshBtns = document.querySelectorAll('.filtro-btn');
  freshBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      freshBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const filtro = btn.dataset.filtro;
      document.querySelectorAll('.tienda-card').forEach(card => {
        if (filtro === 'todos' || card.dataset.cat === filtro) {
          card.style.display = '';
          card.style.animation = 'fade-in-up .35s ease forwards';
        } else {
          card.style.display = 'none';
        }
      });
    });
  });
}

// ─── Modal Pre-Checkout ──────────────────────────────────────────────────────
let checkoutLiderId = null; // Will hold validated leader UUID if found

function abrirCheckoutModal() {
  // Close cart drawer first
  cerrarCarrito();
  // Open modal
  const modal = document.getElementById('checkoutModal');
  const overlay = document.getElementById('checkoutOverlay');
  if (overlay) {
    overlay.style.display = 'block';
    overlay.style.pointerEvents = 'auto';
    overlay.style.visibility = 'visible';
    void overlay.offsetWidth;
    overlay.classList.add('visible');
  }
  if (modal) {
    modal.style.display = 'block';
    modal.style.pointerEvents = 'auto';
    modal.style.visibility = 'visible';
    void modal.offsetWidth;
    modal.classList.add('open');
  }
  document.body.style.overflow = 'hidden';
  // Populate shipping options
  poblarOpcionesEnvio();
}

function poblarOpcionesEnvio() {
  const sel = document.getElementById('checkoutEnvio');
  const hint = document.getElementById('checkoutEnvioHint');
  if (!sel) return;

  sel.innerHTML = '';
  // Option 0: always available CEDIS pickup (free)
  const optRecoger = document.createElement('option');
  optRecoger.value = 'recoger_cedis';
  optRecoger.textContent = '🏢 Recoger en CEDIS — Gratis';
  optRecoger.style.background = '#1a1a2e';
  sel.appendChild(optRecoger);

  if (_enviosDisponibles.length === 0) {
    const optNA = document.createElement('option');
    optNA.value = '';
    optNA.textContent = '📭 Sin opciones de envío disponibles';
    optNA.style.background = '#1a1a2e';
    optNA.disabled = true;
    sel.appendChild(optNA);
    if (hint) hint.textContent = 'Solo disponible recolección en CEDIS por ahora.';
    return;
  }

  _enviosDisponibles.forEach(env => {
    const precio = precioRegional(env);
    if (precio <= 0) return;
    const opt = document.createElement('option');
    opt.value = env.sku || `envio-${env.id}`;
    opt.dataset.precio = precio.toFixed(2);
    opt.dataset.productoId = env.id;
    opt.textContent = `📦 ${env.nombre} — ${formatPrecioRegional(precio)}`;
    opt.style.background = '#1a1a2e';
    sel.appendChild(opt);
  });

  sel.addEventListener('change', () => {
    const selected = sel.options[sel.selectedIndex];
    if (selected.value === 'recoger_cedis') {
      if (hint) hint.textContent = 'Sin costo de envío. Recoge tu pedido en el CEDIS más cercano.';
    } else if (selected.dataset.precio) {
      if (hint) hint.textContent = `Costo de envío: ${formatPrecioRegional(parseFloat(selected.dataset.precio))}`;
    }
  });

  if (hint) hint.textContent = 'Selecciona cómo quieres recibir tu pedido.';
}

function cerrarCheckoutModal() {
  const modal = document.getElementById('checkoutModal');
  const overlay = document.getElementById('checkoutOverlay');
  if (modal) {
    modal.classList.remove('open');
    modal.style.pointerEvents = 'none';
    modal.style.visibility = 'hidden';
    modal.style.display = 'none';
  }
  if (overlay) {
    overlay.classList.remove('visible');
    overlay.style.pointerEvents = 'none';
    overlay.style.visibility = 'hidden';
    overlay.style.display = 'none';
  }
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  document.body.style.removeProperty('overflow');
  document.documentElement.style.removeProperty('overflow');
}

async function buscarLider() {
  const input = document.getElementById('checkoutLiderId');
  const hint = document.getElementById('checkoutLiderHint');
  const registro = input?.value?.trim();
  if (!registro) {
    hint.textContent = 'Si ya eres líder, ingresa tu número de registro para vincular la compra.';
    hint.className = 'checkout-lider-hint';
    checkoutLiderId = null;
    return;
  }
  hint.textContent = '🔍 Buscando...';
  hint.className = 'checkout-lider-hint';
  try {
    const res = await fetch('https://n8n.duletias3rver.com/webhook/bo/sql-relay', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        secret: 'duletia_relay_2026',
        sql: `SELECT id, nombre, ape_p, email, tel FROM cliente WHERE registro = ${parseInt(registro)} LIMIT 1`
      })
    });
    const data = await res.json();
    if (data.rows && data.rows.length > 0) {
      const lider = data.rows[0];
      checkoutLiderId = lider.id;
      hint.textContent = `✅ ¡Encontrado! ${lider.nombre} ${lider.ape_p || ''}`.trim();
      hint.className = 'checkout-lider-hint found';
      // Autocomplete fields
      if (lider.nombre) document.getElementById('checkoutNombre').value = `${lider.nombre} ${lider.ape_p || ''}`.trim();
      if (lider.email) document.getElementById('checkoutEmail').value = lider.email;
      if (lider.tel) document.getElementById('checkoutTel').value = lider.tel;
    } else {
      checkoutLiderId = null;
      hint.textContent = '❌ No se encontró un líder con ese ID. Puedes continuar como nuevo cliente.';
      hint.className = 'checkout-lider-hint error';
    }
  } catch(e) {
    console.error('Error buscando líder', e);
    hint.textContent = '⚠️ Error de conexión. Puedes continuar sin vincular.';
    hint.className = 'checkout-lider-hint error';
    checkoutLiderId = null;
  }
}

// ─── Pagar con Stripe (llamado desde modal pre-checkout) ─────────────────────
async function pagarConStripe() {
  const nombre = document.getElementById('checkoutNombre')?.value?.trim();
  const email = document.getElementById('checkoutEmail')?.value?.trim();
  const tel = document.getElementById('checkoutTel')?.value?.trim();

  // Validación
  if (!nombre || !email || !tel) {
    alert('⚠️ Por favor completa todos los campos obligatorios (nombre, email y teléfono).');
    return;
  }
  if (!email.includes('@')) {
    alert('⚠️ Ingresa un email válido.');
    return;
  }

  // Envío seleccionado
  const envioSel = document.getElementById('checkoutEnvio');
  const envioValue = envioSel?.value || 'recoger_cedis';
  const envioOpt = envioSel?.options[envioSel.selectedIndex];
  const envioPrecio = parseFloat(envioOpt?.dataset?.precio || '0');
  const envioProductoId = envioOpt?.dataset?.productoId || null;

  const btn = document.getElementById('checkoutBtnContinuar');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = `<span class="spinner-inline"></span> Conectando con Stripe...`;
  }

  const items = Object.entries(carrito).map(([id, quantity]) => {
    const p = CATALOGO[id];
    return {
      id,
      quantity,
      nombre: p?.nombre || 'Producto Dulet',
      precio: p?.precio || 0,
      producto_id: p?.producto_id || null,
      sku: p?.sku || id
    };
  });

  try {
    const webhookUrl = 'https://n8n.duletias3rver.com/webhook/checkout-session';

    const res = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items,
        nombre,
        email,
        tel,
        lider_id: checkoutLiderId || '',
        lider_registro: document.getElementById('checkoutLiderId')?.value?.trim() || '',
        esLiderExistente: !!checkoutLiderId,  // true si se validó un líder existente en la BD
        envio: {
          tipo: envioValue,
          precio: envioPrecio,
          producto_id: envioProductoId,
          incluye_iva: true
        },
        moneda: REGION.moneda.toLowerCase(),  // mxn | usd | gtq
        pais: REGION_CODE                     // MX | US | GT
      }),
    });

    const data = await res.json();
    
    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error(data.error || 'No se recibió la URL de Stripe desde n8n');
    }
  } catch (err) {
    console.error(err);
    alert('❌ Error al procesar el pago: ' + err.message + '\n\nSi crees que esto es un error, contacta a soporte.');
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> Continuar al pago 💳`;
    }
  }
}

// ─── Inicialización ───────────────────────────────────────────────────────────
function initTienda() {
  actualizarUI();
  initFiltros();

  document.getElementById('btn-abrir-carrito')?.addEventListener('click', abrirCarrito);
  document.getElementById('cart-nav-btn')?.addEventListener('click', abrirCarrito);
  document.getElementById('cart-overlay')?.addEventListener('click', cerrarCarrito);
  document.getElementById('cart-close')?.addEventListener('click', cerrarCarrito);
  document.getElementById('btn-seguir-comprando')?.addEventListener('click', cerrarCarrito);
  // "Pagar ahora" in cart → opens checkout modal
  document.getElementById('btn-pagar')?.addEventListener('click', abrirCheckoutModal);
  document.getElementById('btn-vaciar')?.addEventListener('click', () => {
    carrito = {};
    guardarCarrito();
    actualizarUI();
  });

  // Delegación de eventos en el grid para garantizar clic 100% en móviles
  const grid = document.getElementById('tiendaGrid');
  if (grid && !grid._hasCarritoDelegation) {
    grid._hasCarritoDelegation = true;
    grid.addEventListener('click', (e) => {
      const btn = e.target.closest('.btn-tienda-carrito');
      if (btn) {
        e.stopPropagation();
        const id = btn.dataset.id || btn.getAttribute('data-id');
        if (id) {
          window.agregarAlCarrito(id);
        }
      }
    });
  }

  // Checkout modal events
  document.getElementById('checkoutClose')?.addEventListener('click', cerrarCheckoutModal);
  document.getElementById('checkoutOverlay')?.addEventListener('click', cerrarCheckoutModal);
  document.getElementById('checkoutBtnLookup')?.addEventListener('click', buscarLider);
  document.getElementById('checkoutLiderId')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); buscarLider(); }
  });
  document.getElementById('checkoutBtnContinuar')?.addEventListener('click', pagarConStripe);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { cerrarProductoBtn(); cerrarCarrito(); cerrarCheckoutModal(); }
  });

  // Detectar ubicación y luego cargar productos
  detectarUbicacion().then(() => {
    mostrarBannerRegion();
    fetchProductosPublicados();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTienda);
} else {
  initTienda();
}

// ─── Banner de Región Detectada ───────────────────────────────────────────────
function mostrarBannerRegion() {
  const tiendaSection = document.getElementById('tienda');
  if (!tiendaSection) return;

  // Crear banner de región con selector
  const banner = document.createElement('div');
  banner.id = 'region-banner';
  banner.style.cssText = `
    display:flex;align-items:center;justify-content:center;gap:0.75rem;flex-wrap:wrap;
    padding:0.65rem 1.25rem;margin-bottom:1rem;
    background:linear-gradient(135deg,rgba(29,78,216,.15),rgba(5,150,105,.1));
    border:1px solid rgba(29,78,216,.2);border-radius:12px;
    font-size:0.85rem;color:rgba(255,255,255,.85);
  `;
  banner.innerHTML = `
    <span>${REGION.bandera} Mostrando precios para <strong>${REGION.nombre}</strong> en <strong>${REGION.moneda}</strong></span>
    <select id="region-selector" style="
      background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.15);border-radius:8px;
      color:#fff;padding:4px 10px;font-size:0.8rem;cursor:pointer;outline:none;
      transition:border-color .2s;
    ">
      ${Object.entries(REGION_MAP).map(([code, r]) =>
        `<option value="${code}" ${code === REGION_CODE ? 'selected' : ''}
          style="background:#1a1a2e;color:#fff;">${r.bandera} ${r.nombre} (${r.moneda})</option>`
      ).join('')}
    </select>
  `;

  // Insertar después del section-header de la tienda
  const header = tiendaSection.querySelector('.section-header');
  if (header && header.nextSibling) {
    header.parentNode.insertBefore(banner, header.nextSibling);
  }

  // Evento de cambio de región
  const selector = document.getElementById('region-selector');
  if (selector) {
    selector.addEventListener('change', (e) => {
      const newCode = e.target.value;
      localStorage.setItem('dulet_region', newCode);
      REGION_CODE = newCode;
      REGION = REGION_MAP[newCode];
      // Re-render products
      recargarTiendaConRegion();
    });
  }
}

// Recargar la tienda con nueva región (sin volver a llamar al webhook)
let _cachedDbProds = null;

function recargarTiendaConRegion() {
  if (!_cachedDbProds) {
    fetchProductosPublicados();
    return;
  }

  // Limpiar grid y filtros
  const grid = document.getElementById('tiendaGrid');
  const filtros = document.querySelector('.tienda-filtros');
  if (grid) grid.innerHTML = '';
  if (filtros) {
    // Mantener solo el botón "Todos"
    const todos = filtros.querySelector('[data-filtro="todos"]');
    filtros.innerHTML = '';
    if (todos) filtros.appendChild(todos);
  }

  // Limpiar catálogo
  Object.keys(CATALOGO).forEach(k => delete CATALOGO[k]);

  // Actualizar banner
  const bannerEl = document.getElementById('region-banner');
  if (bannerEl) {
    const span = bannerEl.querySelector('span');
    if (span) span.innerHTML = `${REGION.bandera} Mostrando precios para <strong>${REGION.nombre}</strong> en <strong>${REGION.moneda}</strong>`;
  }

  // Re-render con datos cacheados
  renderizarProductos(_cachedDbProds);
  // Actualizar badges del showcase con la nueva región
  actualizarPreciosShowcase(_cachedDbProds);
}

// ─── Fetch TODOS los Productos Publicados (BD) ─────────────────────────────
async function fetchProductosPublicados() {
  const grid = document.getElementById('tiendaGrid');
  const loading = document.getElementById('tiendaLoading');

  try {
    const res = await fetch('https://n8n.duletias3rver.com/webhook/public/products/published');
    const dbProds = await res.json();

    // Cache for region switching
    _cachedDbProds = dbProds;

    // Remove loading
    if (loading) loading.remove();

    if (!Array.isArray(dbProds) || dbProds.length === 0) {
      if (grid) grid.innerHTML = `
        <div style="grid-column: 1 / -1; text-align: center; padding: 60px 20px;">
          <div style="font-size: 2rem; margin-bottom: 12px;">📭</div>
          <p style="color: rgba(255,255,255,0.5);">No hay productos disponibles en este momento.</p>
        </div>`;
      return;
    }

    renderizarProductos(dbProds);

  } catch(e) {
    console.error("Error fetching productos publicados", e);
    if (loading) loading.innerHTML = `
      <div style="font-size: 2rem; margin-bottom: 12px;">⚠️</div>
      <p style="color: rgba(255,255,255,0.5);">Error al cargar productos. Intenta recargar la página.</p>`;
  }
}

// ─── Render de productos con precios regionales ──────────────────────────────
function renderizarProductos(dbProds) {
  const grid = document.getElementById('tiendaGrid');

  // ─── Dynamic Category Detection ───────────────────────────────
  const knownCats = {
    'suero':  { slug: 'sueros',  bg: '#1D4ED8', emoji: '🥛', label: 'Sueros',        order: 1 },
    'sueros': { slug: 'sueros',  bg: '#1D4ED8', emoji: '🥛', label: 'Sueros',        order: 1 },
    'esencia':  { slug: 'esencias', bg: '#059669', emoji: '🍃', label: 'Esencias',   order: 2 },
    'esencias': { slug: 'esencias', bg: '#059669', emoji: '🍃', label: 'Esencias',   order: 2 },
    'paquete':  { slug: 'paquetes', bg: '#DC2626', emoji: '📦', label: 'Paquetes',   order: 3 },
    'paquetes': { slug: 'paquetes', bg: '#DC2626', emoji: '📦', label: 'Paquetes',   order: 3 },
    'cajas':    { slug: 'paquetes', bg: '#DC2626', emoji: '📦', label: 'Paquetes',   order: 3 },
    'caja':     { slug: 'paquetes', bg: '#DC2626', emoji: '📦', label: 'Paquetes',   order: 3 },
    'promo':         { slug: 'promo', bg: '#8B5CF6', emoji: '🎁', label: 'Promocionales', order: 90 },
    'promocional':   { slug: 'promo', bg: '#8B5CF6', emoji: '🎁', label: 'Promocionales', order: 90 },
    'promocionales': { slug: 'promo', bg: '#8B5CF6', emoji: '🎁', label: 'Promocionales', order: 90 },
    'promocion':     { slug: 'promo', bg: '#8B5CF6', emoji: '🎁', label: 'Promocionales', order: 90 },
  };
  const autoColors = ['#0891B2','#D97706','#7C3AED','#BE185D','#15803D','#B91C1C','#4338CA'];
  let autoColorIdx = 0;

  const catRegistry = {};
  dbProds.forEach(p => {
    const raw = (p.categoria || '').toLowerCase().trim();
    if (!raw) return;
    const known = knownCats[raw];
    if (known) {
      if (!catRegistry[known.slug]) {
        catRegistry[known.slug] = { bg: known.bg, emoji: known.emoji, label: known.label, order: known.order };
      }
    } else {
      if (!catRegistry[raw]) {
        const color = autoColors[autoColorIdx % autoColors.length];
        autoColorIdx++;
        const label = raw.charAt(0).toUpperCase() + raw.slice(1);
        catRegistry[raw] = { bg: color, emoji: '🏷️', label, order: 50 + autoColorIdx };
      }
    }
  });

  const catOrder = Object.entries(catRegistry)
    .sort(([,a],[,b]) => a.order - b.order)
    .map(([slug]) => slug);

  function resolveCat(raw) {
    const key = (raw || '').toLowerCase().trim();
    const known = knownCats[key];
    if (known) return known.slug;
    if (catRegistry[key]) return key;
    return catOrder[catOrder.length - 1] || 'otros';
  }

  function getBadge(slug) {
    const info = catRegistry[slug];
    return info || { bg: '#6B7280', emoji: '🏷️', label: slug };
  }

  const foundCats = new Set();
  let productosVisibles = 0;

  // Sort products
  dbProds.sort((a, b) => {
    const catA = resolveCat(a.categoria);
    const catB = resolveCat(b.categoria);
    const idxA = catOrder.indexOf(catA);
    const idxB = catOrder.indexOf(catB);
    if (idxA !== idxB) return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    return (a.nombre || '').localeCompare(b.nombre || '');
  });

  dbProds.forEach(dbP => {
    // ── Productos de categoría 'envios' → NO se muestran en grid, se guardan aparte ──
    const catRaw = (dbP.categoria || '').toLowerCase().trim();
    if (catRaw === 'envio' || catRaw === 'envios' || catRaw === 'envío' || catRaw === 'envíos') {
      _enviosDisponibles.push(dbP);
      return; // No mostrar en grid público
    }

    // ── GEOLOCALIZACIÓN: obtener precio regional ──
    const precioLocal = precioRegional(dbP);
    const activoEnRegion = productoActivoEnRegion(dbP);

    // Si precio = 0 o producto inactivo en la región → NO MOSTRAR
    if (precioLocal <= 0 || !activoEnRegion) {
      return; // Skip silently
    }

    const carritoId = dbP.sku || `prod-${dbP.id}`;
    const dataCat = resolveCat(dbP.categoria);
    const badge = getBadge(dataCat);

    foundCats.add(dataCat);
    productosVisibles++;

    let imgSrc = '';
    const CDN_BASE = 'https://cdn.shopify.com/s/files/1/0947/4172/5498/t/9/assets/prod-';

    if (dbP.imagen_url && dbP.imagen_url.startsWith('http')) {
      imgSrc = dbP.imagen_url;
    } else if (dbP.imagen_url) {
      imgSrc = dbP.imagen_url.includes('cdn.shopify') ? dbP.imagen_url : `${CDN_BASE}${dbP.imagen_url}`;
    } else {
      imgSrc = `${CDN_BASE}${dbP.sku}.png`;
    }

    // ── i18n: usar traducción inglés si región USA/CA ──
    const isEnglish = REGION_CODE === 'US' || REGION_CODE === 'CA';
    const nombreDisplay = isEnglish
      ? (dbP.nombre_en ? limpiarNombreProducto(dbP.nombre_en) : limpiarNombreProducto(dbP.nombre))
      : limpiarNombreProducto(dbP.nombre);
    const descDisplay = isEnglish
      ? (dbP.descripcion_en || dbP.descripcion || dbP.categoria || 'Official Product')
      : (dbP.descripcion || dbP.categoria || 'Producto oficial');

    // Register in CATALOGO with regional price
    CATALOGO[carritoId] = {
      nombre: nombreDisplay,
      detalle: descDisplay,
      precio: precioLocal,
      emoji: badge.emoji,
      img: imgSrc,
      imgs: [imgSrc],
      descripcion: descDisplay,
      features: isEnglish ? ['Official Product', 'Dulet International Group'] : ['Producto Oficial', 'Dulet International Group'],
      categoria: dataCat,
      carritoId: carritoId,
      producto_id: dbP.id,
      sku: dbP.sku,
      shopUrl: '#',
      moneda: REGION.moneda
    };

    // Create DOM card with regional price
    if (grid) {
      try {
        const div = document.createElement('div');
        div.className = 'tienda-card';
        div.dataset.cat = dataCat;
        div.dataset.carritoId = carritoId;

        div.innerHTML = `
          <div class="tienda-card-img-wrap" style="cursor:pointer;" title="${isEnglish ? 'View details' : 'Ver detalles'}">
              <img src="${imgSrc}" alt="${nombreDisplay}" onerror="this.onerror=null;this.src='https://cdn.shopify.com/s/files/1/0947/4172/5498/t/9/assets/prod-paquete.png';" loading="lazy">
              <div class="tienda-card-overlay"><span>${isEnglish ? '👁️ View details' : '👁️ Ver detalles'}</span></div>
              <div class="tienda-badge-cat" style="background:${badge.bg}">${badge.emoji} ${badge.label}</div>
          </div>
          <div class="tienda-card-body">
              <h4 style="cursor:pointer;">${nombreDisplay}</h4>
              <p>${descDisplay}</p>
              <div class="tienda-price">${formatPrecioRegional(precioLocal)}</div>
              <button type="button" class="btn-tienda-carrito" data-id="${carritoId}" onclick="event.stopPropagation(); window.agregarAlCarrito('${carritoId}');">${isEnglish ? '🛒 Add to cart' : '🛒 Agregar'}</button>
          </div>
        `;

        // Click en imagen o título abre modal de detalle
        const imgWrap = div.querySelector('.tienda-card-img-wrap');
        if (imgWrap) {
          imgWrap.addEventListener('click', (e) => {
            e.stopPropagation();
            abrirProducto(carritoId);
          });
        }
        const titleEl = div.querySelector('.tienda-card-body h4');
        if (titleEl) {
          titleEl.addEventListener('click', (e) => {
            e.stopPropagation();
            abrirProducto(carritoId);
          });
        }

        // Botón agregar al carrito: directo, limpio y blindado
        const btn = div.querySelector('.btn-tienda-carrito');
        if (btn) {
          btn.onclick = function(e) {
            if (e) e.stopPropagation();
            window.agregarAlCarrito(carritoId);
          };
        }

        grid.appendChild(div);
      } catch (errCard) {
        console.error('Error renderizando card de producto', dbP, errCard);
      }
    }
  });

  // Si no hay productos visibles para esta región
  if (productosVisibles === 0 && grid) {
    grid.innerHTML = `
      <div style="grid-column: 1 / -1; text-align: center; padding: 60px 20px;">
        <div style="font-size: 2rem; margin-bottom: 12px;">${REGION.bandera}</div>
        <p style="color: rgba(255,255,255,0.5);">No hay productos disponibles para ${REGION.nombre} en este momento.</p>
        <p style="color: rgba(255,255,255,0.35);font-size:0.85rem;margin-top:8px;">Intenta seleccionar otra región desde el selector arriba.</p>
      </div>`;
  }

  // Build filter buttons
  const filtrosCont = document.querySelector('.tienda-filtros');
  if (filtrosCont) {
    catOrder.forEach(cat => {
      if (foundCats.has(cat)) {
        const info = catRegistry[cat];
        const btn = document.createElement('button');
        btn.className = 'filtro-btn';
        btn.dataset.filtro = cat;
        btn.textContent = `${info.emoji} ${info.label}`;
        filtrosCont.appendChild(btn);
      }
    });
    initFiltros();
  }

  // Update cart
  actualizarUI();

  // Actualizar precios del showcase de productos con moneda regional
  actualizarPreciosShowcase(dbProds);

  console.log(`🏪 Tienda: ${productosVisibles}/${dbProds.length} productos visibles para ${REGION.bandera} ${REGION.nombre}`);
}

// ─── Actualizar badges de precio en el showcase de Productos ─────────────────
// Lee el precio mínimo de sueros y esencias desde los datos de la BD
// y los muestra en la moneda regional correcta.
function actualizarPreciosShowcase(dbProds) {
  if (!Array.isArray(dbProds) || dbProds.length === 0) return;

  // Categorías que corresponden a sueros (Dulet) y esencias (Eucament)
  const esSuero = cat => ['suero','sueros'].includes((cat||'').toLowerCase().trim());
  const esEsencia = cat => ['esencia','esencias'].includes((cat||'').toLowerCase().trim());

  // Encontrar precio mínimo disponible en la región actual
  let minDulet = Infinity;
  let minEucament = Infinity;

  dbProds.forEach(p => {
    const cat = (p.categoria || '').toLowerCase().trim();
    const precio = precioRegional(p);
    const activo = productoActivoEnRegion(p);
    if (!activo || precio <= 0) return;

    if (esSuero(cat) && precio < minDulet)     minDulet = precio;
    if (esEsencia(cat) && precio < minEucament) minEucament = precio;
  });

  // Helpers de formato compact (sin decimales para el badge)
  function formatBadge(monto, region) {
    if (region.moneda === 'GTQ') return `Desde Q${Math.round(monto).toLocaleString('es-GT')}`;
    return `Desde ${region.simbolo}${Math.round(monto).toLocaleString('es-MX')}`;
  }
  function formatCurrency(region) {
    return `${region.moneda} · ${region.bandera}`;
  }

  // Actualizar badge Dulet
  const duletPrice = document.getElementById('showcase-dulet-price');
  const duletCur   = document.getElementById('showcase-dulet-currency');
  if (duletPrice && minDulet !== Infinity) {
    duletPrice.textContent = formatBadge(minDulet, REGION);
    if (duletCur) duletCur.textContent = formatCurrency(REGION) + ' · Dulet';
  }

  // Actualizar badge Eucament
  const eucaPrice = document.getElementById('showcase-eucament-price');
  const eucaCur   = document.getElementById('showcase-eucament-currency');
  if (eucaPrice && minEucament !== Infinity) {
    eucaPrice.textContent = formatBadge(minEucament, REGION);
    if (eucaCur) eucaCur.textContent = formatCurrency(REGION) + ' · Eucament';
  }

  console.log(`💰 Showcase: Dulet=${minDulet === Infinity ? 'N/A' : formatBadge(minDulet, REGION)}, Eucament=${minEucament === Infinity ? 'N/A' : formatBadge(minEucament, REGION)} [${REGION.moneda}]`);
}

// ─── Exponer al objeto window para compatibilidad inline y global ─────────────
window.agregarAlCarrito = agregarAlCarrito;
window.abrirCarrito = abrirCarrito;
window.cerrarCarrito = cerrarCarrito;
window.actualizarCarrito = actualizarCarrito;
window.actualizarUI = actualizarUI;
window.abrirProducto = abrirProducto;
window.cerrarProductoBtn = cerrarProductoBtn;
window.cambiarCantidad = cambiarCantidad;
window.quitarDelCarrito = quitarDelCarrito;
window.CATALOGO = CATALOGO;

